import type { StatusVariant } from "@/lib/colors";
import type { AgentState, ConnectorInfo, MetricValue, SyncStatus } from "./types";

export const AGENT_SYNC_ELEMENT_ID = "sidebar.synchronizacja-apteki";
/** Przycisk i kolejka masowego sprawdzenia (F1 §7) — grant per rola jak każdy element strony. */
export const MANUAL_CHECK_ELEMENT_ID = "page.synchronizacja-apteki.masowe-sprawdzenie";

export const STATUS_LABEL: Record<SyncStatus, string> = {
	ok: "zgodne",
	warn: "różnica ≤ 1 %",
	mismatch: "rozjazd",
	pending: "w toku",
	"n/a": "brak danych",
	unreachable: "nieosiągalna",
	archive: "archiwum",
};

/** `pending` celowo informacyjny (D36: dzień bieżący bez czerwieni); `archive` szary (D40). */
export const STATUS_VARIANT: Partial<Record<SyncStatus, StatusVariant>> = {
	ok: "success",
	warn: "warning",
	mismatch: "error",
	pending: "info",
};

/** Kolor słupka / kropki trendu — tylko tokeny statusów. */
export const STATUS_FILL: Record<SyncStatus, string> = {
	ok: "bg-status-success-text",
	warn: "bg-status-warning-text",
	mismatch: "bg-status-error-text",
	pending: "bg-status-info-text",
	"n/a": "bg-muted-foreground",
	unreachable: "bg-muted-foreground",
	archive: "bg-muted-foreground",
};

/**
 * Opcje filtra kolumn statusów listy — wartości z whitelisty kontraktu.
 * `pending` jako najgorszy = dzień przeszły, którego daemon nie przeliczył po północy;
 * `unreachable` występuje tylko w parach ze stroną A; `archive` (D40) = dni tylko w Target.
 */
export const WORST_STATUS_OPTIONS = [
	{ value: "mismatch", label: STATUS_LABEL.mismatch },
	{ value: "warn", label: STATUS_LABEL.warn },
	{ value: "unreachable", label: STATUS_LABEL.unreachable },
	{ value: "pending", label: STATUS_LABEL.pending },
	{ value: "ok", label: STATUS_LABEL.ok },
	{ value: "archive", label: STATUS_LABEL.archive },
	{ value: "n/a", label: STATUS_LABEL["n/a"] },
];

/**
 * Etykiety par stron — kolejność jak w tooltipie komórki (A→T nadrzędna, D39).
 * Dwie drogi danych do Target: Apteka → Target (agent-sync, droga docelowa)
 * i Source → Target (Robot, droga obecna). Apteka ↔ Source jest tylko pomocnicza:
 * wskazuje, czy rozjazd powstał już u starego robota ERP → Source.
 */
export const PAIR_LABEL = {
	at: "Apteka → Target",
	as: "Apteka ↔ Source",
	st: "Source → Target",
} as const;

/** Opis drogi pod etykietą pary — kafle dróg w sekcji narzędzi panelu apteki. */
export const PAIR_ROLE = {
	at: "agent-sync · droga docelowa",
	as: "pomocniczo · czy błąd jest u starego robota",
	st: "Robot · droga obecna",
} as const;

/** `2021-04-01` → `04.2021`. */
function monthLabel(iso: string): string {
	return `${iso.slice(5, 7)}.${iso.slice(0, 4)}`;
}

/** Ostatni miesiąc archiwum apteki („do 03.2021”) — miesiąc dnia przed granicą `cut`. */
export function pharmacyArchiveUntil(cut: string): string {
	return monthLabel(addDays(cut, -1));
}

/** Kto ustawił poświadczenia: nazwa z backendu, inaczej „użytkownik #<id>”. */
export function credentialsSetByLabel(
	c: Pick<ConnectorInfo, "credentialsSetBy" | "credentialsSetByName">,
): string | null {
	if (c.credentialsSetByName) return c.credentialsSetByName;
	if (c.credentialsSetBy == null || c.credentialsSetBy === "") return null;
	return `użytkownik #${c.credentialsSetBy}`;
}

export const VARIANT_LABEL: Record<string, string> = {
	filtered: "bez usuniętych i buforowych (wskus = 0, bufor = 0)",
	all: "wszystkie wiersze",
};

export const METRIC_LABEL: Record<string, string> = {
	count: "liczba wierszy",
	sum_ilosp: "Σ ilość sprzedana",
	sum_brutto: "Σ wartość brutto [zł]",
	sum_netto: "Σ wartość netto [zł]",
	sum_marzao: "Σ marża [zł]",
	count_zwrot: "zwroty (typsp 24, 25)",
	sum_zplcl: "Σ zapłacone przez pacjenta [zł]",
	sum_wrtbn: "Σ bonifikata [zł]",
	sum_cenaz_ilosp: "Σ wartość zakupu sprzedanych [zł]",
	sum_ilzkl: "Σ ilość zakupiona",
	sum_ilakt: "Σ ilość aktualna",
	sum_czfnt_ilzkl: "Σ cena zakupu netto × ilość [zł]",
	sum_ilakt_czfnt: "Σ stan × cena zakupu netto [zł]",
	sum_mnozn: "Σ mnożnik opakowania",
	sum_wstrz: "Σ ilość wstrzymana",
	sum_czfbr_ilzkl: "Σ cena zakupu brutto × ilość [zł]",
	count_tpvat1: "dokumenty VAT (tpvat = 1)",
	count_korekty: "dokumenty korygujące (tpkor ≠ 0)",
	count_datap: "dokumenty z terminem płatności",
	sum_ilak2: "Σ ilość korekty",
	sum_czfn2: "Σ cena korekty netto [zł]",
	sum_ilzp_zwrot: "Σ ilość zwrócona (ilzp1 − ilzp2)",
	sum_korekta_netto: "Σ korekta ceny netto × ilość [zł]",
	count_tpkor: "liczba wg typu korekty",
	count_wskus0: "aktywne karty (wskus = 0)",
	count_bloz: "karty z kodem BLOZ",
	sum_wrtbf: "Σ brutto z faktury [zł]",
	sum_wrtnf: "Σ netto z faktury [zł]",
	sum_wrtvf: "Σ VAT z faktury [zł]",
	sum_wrtbt: "Σ brutto skorygowane przez aptekę [zł]",
	count_month: "liczba wg miesiąca dgakt",
	sum_cenad: "Σ cena detaliczna [zł]",
	count_cnkn: "liczba cenników",
	per_cnkn: "wg cennika",
	max_dgakt: "ostatnia zmiana (dgakt)",
};

const WARSAW = "Europe/Warsaw";

/** Dzisiejsza data w Polsce (`YYYY-MM-DD`) — oś dnia bieżącego w daemonie. */
export function todayWarsaw(now = new Date()): string {
	return new Intl.DateTimeFormat("sv-SE", { timeZone: WARSAW }).format(now);
}

export function addDays(iso: string, days: number): string {
	const d = new Date(`${iso}T12:00:00Z`);
	d.setUTCDate(d.getUTCDate() + days);
	return d.toISOString().slice(0, 10);
}

export function daysBetween(from: string, to: string): number {
	return Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000);
}

/** Czas ISO (UTC) → „07.10 22:47” w czasie polskim. */
export function fmtDateTime(iso: string | null | undefined): string {
	if (!iso) return "—";
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return iso;
	return d.toLocaleString("pl-PL", {
		timeZone: WARSAW,
		day: "2-digit",
		month: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
	});
}

/** `2026-10-05` → `05.10`. */
export function fmtDayShort(iso: string): string {
	const [, m, d] = iso.split("-");
	return d && m ? `${d}.${m}` : iso;
}

/** `2024-12-31` → `31.12.2024` (granice archiwum sięgają innych lat). */
export function fmtDateFull(iso: string | null | undefined): string {
	if (!iso) return "—";
	const [y, m, d] = iso.slice(0, 10).split("-");
	return y && m && d ? `${d}.${m}.${y}` : iso;
}

/** `2024-03-01` → `03.2024`. */
export function fmtMonth(month: string): string {
	const [y, m] = month.split("-");
	return y && m ? `${m}.${y}` : month;
}

/** Ostatni dzień miesiąca `YYYY-MM-01`, nie później niż `today`. */
export function monthEnd(month: string, today: string): string {
	const [y, m] = month.split("-").map(Number);
	const last = new Date(Date.UTC(y ?? 1970, m ?? 1, 0)).toISOString().slice(0, 10);
	return last < today ? last : today;
}

export function fmtLag(minutes: number | null | undefined): string {
	if (minutes == null) return "—";
	if (Math.abs(minutes) < 60) return `${minutes} min`;
	const h = Math.trunc(minutes / 60);
	const m = Math.abs(minutes % 60);
	return m ? `${h} h ${m} min` : `${h} h`;
}

export function fmtCount(v: number | null | undefined): string {
	return v == null ? "—" : v.toLocaleString("pl-PL");
}

/** Metryka: liczba → locale, dokładny tekst numeric → przecinek dziesiętny, mapa → `k: v`. */
export function fmtMetric(v: MetricValue | undefined): string {
	if (v == null) return "—";
	if (typeof v === "number") return v.toLocaleString("pl-PL");
	if (typeof v === "string") return /^-?\d+(\.\d+)?$/.test(v) ? v.replace(".", ",") : v;
	return Object.entries(v)
		.map(([k, inner]) => {
			if (inner != null && typeof inner === "object") {
				const parts = Object.entries(inner as Record<string, unknown>)
					.map(([ik, iv]) => `${ik} ${fmtMetric(iv as MetricValue)}`)
					.join(", ");
				return `${k}: ${parts}`;
			}
			return `${k}: ${fmtMetric(inner as MetricValue)}`;
		})
		.join(" · ");
}

export function metricLabel(id: string): string {
	return METRIC_LABEL[id] ?? id;
}

/** Nazwa biznesowa tabeli ERP (rejestr danych apteki, `doc/robot/REJESTR_DANYCH_APTEKI.md`). */
export const TABLE_LABEL: Record<string, string> = {
	sprz: "sprzedaż",
	kzak: "zakupy i stan magazynu",
	dokf: "dokumenty (faktury)",
	korz: "korekty zakupów",
	towr: "kartoteka towarów",
	cnkp: "pozycje cenników",
	paca: "pacjenci i odbiorcy",
	dost: "dostawcy",
	hurt: "hurtownie",
	pers: "personel",
	dedf: "definicje dokumentów",
	grpn: "grupy towarów",
	grpp: "przypisania do grup",
	hstw: "historia zmian towarów",
	leka: "lekarze",
	parm: "parametry systemu",
	prmn: "promocje",
	tabv: "VAT dokumentów",
	zwpz: "zwroty",
};

export function tableLabel(table: string): string {
	return TABLE_LABEL[table] ?? table;
}

/** Próg świeżości kontaktu usługi agent-sync (heartbeat co 60 s): ≤ 3 min zielony, ≤ 15 min żółty. */
const CONTACT_OK_MINUTES = 3;
const CONTACT_WARN_MINUTES = 15;

export function contactTone(
	lastSeenAt: string | null | undefined,
	now: number = Date.now(),
): "success" | "warning" | "error" {
	if (!lastSeenAt) return "error";
	const minutes = (now - Date.parse(lastSeenAt)) / 60_000;
	if (!Number.isFinite(minutes)) return "error";
	if (minutes <= CONTACT_OK_MINUTES) return "success";
	if (minutes <= CONTACT_WARN_MINUTES) return "warning";
	return "error";
}

/** „przed chwilą”, „N min temu”, „N h temu”, dalej data — wiek kontaktu bez zegara na ekranie. */
export function fmtAgo(iso: string | null | undefined, now: number = Date.now()): string {
	if (!iso) return "brak";
	const minutes = Math.floor((now - Date.parse(iso)) / 60_000);
	if (!Number.isFinite(minutes)) return "brak";
	if (minutes < 1) return "przed chwilą";
	if (minutes < 60) return `${minutes} min temu`;
	if (minutes < 48 * 60) return `${Math.floor(minutes / 60)} h temu`;
	return fmtDateTime(iso);
}

// ── F2: strona A, usługa sync, konektor ─────────────────────────────────────

/** Etykiety `agentState` (lista) i `serviceState` (binding). */
export const AGENT_STATE_LABEL: Record<string, string> = {
	none: "brak agenta",
	disabled: "wyłączona",
	starting: "uruchamia się",
	running: "działa",
	stopped: "zatrzymana",
	error: "błąd",
	engine_drift: "dryf silnika",
	paused: "wstrzymana",
};

export const AGENT_STATE_VARIANT: Partial<Record<string, StatusVariant>> = {
	starting: "info",
	running: "success",
	stopped: "warning",
	error: "error",
	engine_drift: "error",
	// D47: pauza to decyzja użytkownika, nie awaria — ostrzeżenie jak odznaka „wstrzymana”.
	paused: "warning",
};

export function agentStateLabel(state: AgentState | null | undefined): string {
	if (!state) return "—";
	return AGENT_STATE_LABEL[state] ?? state;
}

/** Kolor kropki stanu usługi (kolumna „Agent” listy) — wariant jak `AGENT_STATE_VARIANT`, tylko tokeny. */
const AGENT_STATE_DOT: Record<StatusVariant, string> = {
	success: "bg-positive",
	error: "bg-negative",
	warning: "bg-status-warning-text",
	info: "bg-status-info-text",
};

/** `none`, `disabled` i stany bez wariantu — neutralna kropka `bg-border`. */
export function agentStateDotClass(state: AgentState | null | undefined): string {
	const variant = state ? AGENT_STATE_VARIANT[state] : undefined;
	return variant ? AGENT_STATE_DOT[variant] : "bg-border";
}

/** Filtr listy „Usługa sync” — whitelist `agentState` (lista) z kontraktu F2 §2a. */
export const AGENT_STATE_OPTIONS = [
	"none",
	"running",
	"starting",
	"stopped",
	"error",
	"engine_drift",
	"paused",
	"disabled",
].map((value) => ({ value, label: AGENT_STATE_LABEL[value] }));

/** Silniki ERP przyjmowane przez backend (`DB_TYPES` w `control.rs`). */
export const DB_TYPE_OPTIONS = [
	{ value: "firebird", label: "Firebird" },
	{ value: "oracle", label: "Oracle" },
	{ value: "mssql", label: "MS SQL Server" },
];

export function dbTypeLabel(value: string | null | undefined): string {
	if (!value) return "—";
	return DB_TYPE_OPTIONS.find((o) => o.value === value.toLowerCase())?.label ?? value;
}

/** Wersja bez zerowego ogona, min. dwa człony: `18.0.0.0.0` → `18.0`, `11.2.0.4.0` → `11.2.0.4`. */
export function engineVersionShort(version: string | null | undefined): string {
	const parts = (version ?? "").trim().split(".");
	while (parts.length > 2 && parts[parts.length - 1] === "0") parts.pop();
	return parts.join(".");
}

/** „Oracle 18.0”, „Firebird 3.0.14”; bez wersji sam silnik. */
export function engineLabel(
	engine: string | null | undefined,
	version: string | null | undefined,
): string {
	const short = engineVersionShort(version);
	return short ? `${dbTypeLabel(engine)} ${short}` : dbTypeLabel(engine);
}

const ENGINE_SHORT: Record<string, string> = { firebird: "FB", oracle: "ORA", mssql: "MSSQL" };

/** Skrót silnika do odznaki zmiany (D42): FB / ORA / MSSQL; nieznany — wielkimi literami. */
export function engineShort(value: string | null | undefined): string {
	if (!value) return "?";
	return ENGINE_SHORT[value.toLowerCase()] ?? value.toUpperCase();
}

/** „zmiana silnika FB→ORA 07.10.2026” — `null`, gdy stanowisko nie zgłosiło zmiany. */
export function engineChangeText(c: {
	engineChangedAt?: string | null;
	engineChangedFrom?: string | null;
	engineChangedTo?: string | null;
}): string | null {
	if (!c.engineChangedAt) return null;
	const d = new Date(c.engineChangedAt);
	// Znacznik czasu (timestamptz) → data w Polsce; sama data `YYYY-MM-DD` bez zmian.
	const day = Number.isNaN(d.getTime()) ? c.engineChangedAt : todayWarsaw(d);
	return `zmiana silnika ${engineShort(c.engineChangedFrom)}→${engineShort(c.engineChangedTo)} ${fmtDateFull(day)}`;
}

/** Silnik lokalny stanowiska (D42): „BDE: Oracle, FM.xml: Oracle”; bez źródeł — „—”. */
export function localEngineText(
	local: { bde: string | null; fmXml: string | null } | null | undefined,
): string {
	if (!local || (!local.bde && !local.fmXml)) return "—";
	return `BDE: ${dbTypeLabel(local.bde)}, FM.xml: ${dbTypeLabel(local.fmXml)}`;
}

export const CREDENTIALS_SOURCE_LABEL: Record<string, string> = {
	panel: "panel",
	fm_xml: "FM.xml na stanowisku",
	helper: "formularz w aptece",
};

export function fmtMs(ms: number | null | undefined): string {
	if (ms == null) return "—";
	if (ms < 1000) return `${Math.round(ms).toLocaleString("pl-PL")} ms`;
	return `${(ms / 1000).toLocaleString("pl-PL", { maximumFractionDigits: 1 })} s`;
}

export function fmtBytes(bytes: number | null | undefined): string {
	if (bytes == null) return "—";
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 ** 2)
		return `${(bytes / 1024).toLocaleString("pl-PL", { maximumFractionDigits: 0 })} KB`;
	return `${(bytes / 1024 ** 2).toLocaleString("pl-PL", { maximumFractionDigits: 1 })} MB`;
}

/** Kadencja godzinowa ze stanu backoffu: 60 min × 2^poziom (kontrakt F2 §3.6). */
export function fmtBackoff(level: number | null | undefined): string {
	if (!level) return "bez backoffu";
	return `backoff ${level} · co ${fmtLag(60 * 2 ** level)}`;
}

const BUILD_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Wersja z build info do wyświetlenia: `0.1.119 · g1a2b3c4 · x86 · firebird,oracle · 2026-10-08`.
 * Build info (`g<sha7>[-dirty] <data> <x86|x64> [sterowniki]`) jest tylko diagnostyczne —
 * kształt spoza kontraktu doklejamy w całości, brak = sama wersja.
 */
export function versionWithBuild(
	version: string | null | undefined,
	buildInfo: string | null | undefined,
): string {
	const v = version || "—";
	const info = buildInfo?.trim();
	if (!info) return v;
	const [sha, date, arch, ...features] = info.split(/\s+/);
	if (!arch || !BUILD_DATE.test(date ?? "")) return `${v} · ${info}`;
	return [v, sha, arch, ...(features.length ? [features.join(" ")] : []), date].join(" · ");
}

/** Rozjazd wersji usługi sync i agenta na tym samym stanowisku (np. po niepełnej aktualizacji). */
export interface VersionMismatch {
	sync: string;
	agent: string;
}

/**
 * Wersja usługi sync z bindingu ≠ wersja agenta urządzenia bindingu. Bez
 * którejkolwiek wersji nie ma czego porównać (`null`). Wyłączony binding też
 * `null`: usługa stoi, jej wersja zostaje z ostatniego raportu, a agent może
 * się w tym czasie zaktualizować — to nie rozjazd do naprawy.
 */
export function syncAgentMismatch(
	binding: { deviceId: string; enabled: boolean; serviceVersion: string | null } | null | undefined,
	devices: readonly { deviceId: string; serviceVersion: string | null }[] | null | undefined,
): VersionMismatch | null {
	const sync = binding?.serviceVersion?.trim();
	if (!binding?.enabled || !sync) return null;
	const agent = devices?.find((d) => d.deviceId === binding.deviceId)?.serviceVersion?.trim();
	if (!agent || agent === sync) return null;
	return { sync, agent };
}

export function mismatchLabel(m: VersionMismatch): string {
	return `usługa sync ${m.sync} ≠ agent ${m.agent}`;
}

export const MISMATCH_HINT =
	"Usługa sync i agent na tym stanowisku mają różne wersje (np. po niepełnej aktualizacji). " +
	"Uruchom ponownie usługę sync; jeśli rozjazd zostaje, zainstaluj agenta ponownie na stanowisku.";

/**
 * Komunikat 409 przy włączaniu stanowiska. `binding_conflict` = inne urządzenie
 * ma już włączoną synchronizację; `activeDeviceId` z `details` błędu (`ApiError`
 * czyta tylko `error`/`code`/`details`), a gdy go nie ma — z bindingu karty.
 * Pozostałe 409 (wersja agenta, urządzenie offline) mają gotowy komunikat backendu.
 * Bez `instanceof ApiError`, żeby moduł formatów nie zależał od klienta HTTP.
 */
export function bindingErrorMessage(
	err: unknown,
	deviceName: (id: string) => string | null,
	fallbackActiveId?: string | null,
): string {
	const e = (err ?? {}) as {
		status?: number;
		code?: string;
		message?: string;
		details?: Record<string, unknown>;
	};
	const isConflict = e.code === "binding_conflict" || e.message === "binding_conflict";
	if (e.status === 409 && isConflict) {
		const raw = e.details?.activeDeviceId;
		const activeId = typeof raw === "string" ? raw : (fallbackActiveId ?? null);
		const name = activeId ? (deviceName(activeId) ?? activeId) : null;
		return name
			? `Synchronizacja jest już włączona na stanowisku „${name}”. Najpierw wyłącz ją tam — apteka ma jedno stanowisko sync.`
			: "Synchronizacja jest już włączona na innym stanowisku tej apteki. Najpierw wyłącz ją tam.";
	}
	// Kody bez polskiego komunikatu po stronie backendu.
	if (e.code === "device_offline" || e.message === "device_offline") {
		return "Urządzenie jest offline — włącz je (agent musi być połączony) i spróbuj ponownie.";
	}
	return e.message || "Nie udało się zmienić stanowiska synchronizacji.";
}

// ── D47: ustawienia stanowiska i katalog ────────────────────────────────────

export const CADENCE_PRESET_LABEL: Record<string, string> = {
	default: "domyślny — co godzinę + noc",
	slow: "wolniejszy — co 4 h + noc",
	nightly_only: "tylko nocą",
};

export const CATALOG_CADENCE_LABEL: Record<string, string> = {
	hourly: "co godzinę + noc",
	nightly: "nocą",
};

export const AXIS_LABEL: Record<string, string> = {
	day: "dzień (data biznesowa)",
	snapshot: "zdjęcie stanu",
};

/** Domyślne okno zakazane usługi (D31), gdy apteka nie ma własnego. */
export const DEFAULT_FORBIDDEN_WINDOW = { from: "02:00", to: "04:00" } as const;

/**
 * Komunikat błędu mutacji stanowiska (D47): 409 z kodem bez polskiego tekstu po
 * stronie backendu dostaje czytelny opis; reszta — komunikat backendu.
 */
export function syncActionErrorMessage(err: unknown, fallback: string): string {
	const e = (err ?? {}) as { status?: number; code?: string; message?: string };
	const code = e.code ?? e.message;
	if (code === "paused") {
		return "Synchronizacja apteki jest wstrzymana — wznów ją w „Ustawieniach synchronizacji”, żeby sprawdzić teraz.";
	}
	if (code === "binding_missing") {
		return "Apteka nie ma stanowiska synchronizacji — najpierw włącz je w karcie „Stanowisko i usługa”.";
	}
	if (code === "binding_disabled") {
		return "Synchronizacja na stanowisku jest wyłączona — włącz ją, żeby zmienić ustawienia.";
	}
	return e.message || fallback;
}

/**
 * Błąd całego zlecenia naprawy (`POST /agent-sync/repairs`: 400/403, sieć).
 * Odmowy pojedynczych pozycji przychodzą w wyniku — `repairRefusalMessage`.
 */
export function repairErrorMessage(err: unknown): string {
	return syncActionErrorMessage(err, "Nie udało się zlecić naprawy.");
}

const REPAIR_REFUSAL_FALLBACK: Record<string, string> = {
	already_queued:
		"Naprawa tego miesiąca jest już w kolejce robota — robot weźmie ją w najbliższym cyklu.",
	not_repairable: "Miesiąc nie kwalifikuje się do naprawy z Source (odśwież kartę).",
	pharmacy_unavailable: "Apteka jest nieaktywna albo wyłączona z Robota — Robot jej nie naprawi.",
	error: "Nie udało się zlecić naprawy tej pozycji — spróbuj ponownie.",
};

/**
 * D64: pozycja zlecenia, której Robot nie przyjął (`already_queued`,
 * `not_repairable` z powodem, `pharmacy_unavailable`, `error`). Polski komunikat
 * backendu ma pierwszeństwo; limitu ponowienia D43 dla zleceń ręcznych nie ma.
 */
export function repairRefusalMessage(r: { result: string; message?: string }): string {
	return r.message || (REPAIR_REFUSAL_FALLBACK[r.result] ?? "Robot nie przyjął zlecenia.");
}

// ── Kompaktowe kafle dróg (sekcja narzędzi panelu apteki) ───────────────────

/** „1 dzień”, „2 dni”, „35 dni”. */
export function fmtDays(n: number): string {
	return n === 1 ? "1 dzień" : `${n.toLocaleString("pl-PL")} dni`;
}

/** Polska odmiana po liczbie: `plural(2, "miesiąc", "miesiące", "miesięcy")` → „2 miesiące”. */
export function plural(n: number, one: string, few: string, many: string): string {
	return `${n.toLocaleString("pl-PL")} ${pluralWord(n, one, few, many)}`;
}

/** Sama odmiana bez liczby — gdy liczba stoi osobno (np. sformatowana `fmtCount`). */
export function pluralWord(n: number, one: string, few: string, many: string): string {
	const tens = n % 100;
	const units = n % 10;
	return n === 1 ? one : units >= 2 && units <= 4 && (tens < 12 || tens > 14) ? few : many;
}

/** „1 miesiąc”, „3 miesiące”, „28 miesięcy”. */
export const fmtMonthsCount = (n: number) => plural(n, "miesiąc", "miesiące", "miesięcy");

/** Krótka nazwa presetu kadencji do nagłówka sekcji („domyślna”, „wolniejsza”, „tylko nocą”). */
export const CADENCE_PRESET_SHORT: Record<string, string> = {
	default: "domyślna",
	slow: "wolniejsza",
	nightly_only: "tylko nocą",
};
